import React from 'react';
import LoadingSpinner from '../../LoadingSpinner';
import "react-table/react-table.css";
import 'bootstrap/dist/css/bootstrap.css';
import ReactTooltip from "react-tooltip";
import NPReactTable from '../../NPReactTable';
import Dialog from 'react-bootstrap-dialog';
import { hasGrant } from '../../util/grantHelper';
import * as Grants from '../../util/grants';
import FontAwesome from 'react-fontawesome';
import * as Convert from '../../util/unitConversion';

class AllocationWeekTotals extends React.Component {
  constructor(props) {
    super(props);
    this.state={
      header: this.props.apiToken ? {
          'Authorization': 'Bearer ' +  this.props.apiToken,
          'Accept': 'application/octet-stream'
      } : {},
      totals: [],
      page: 0,
      pageSize: 1000, 
      resized: [],
      selectedYear: {},
      availableYears: this.props.allocationMainWrapper ? this.props.allocationMainWrapper.AvailableYears : [],
      isLoading: false,
    };
    this.initialize = this.initialize.bind(this);
    this.initialize();
  }

  createYearEntry() {
    const yearWeeks = this.props.data.Months.reduce((acc, month) => {
      return acc.concat(month.Weeks);
    }, []);
    return {
      Month: 13,
      Weeks: yearWeeks,
      Capacity: this.props.data.YearCapacity,
      ActualCapacity: this.props.data.YearActualCapacity,
      Allocation: this.props.data.YearAllocation,
      AllocationBudget: this.props.data.YearAllocationBudget,
      Ordered: this.props.data.YearOrdered,
      DifferenceAllocatedOrdered: this.props.data.YearDifferenceAllocatedOrdered,
      DifferenceCapacityAllocated: this.props.data.YearDifferenceCapacityAllocated,
      Produced: this.props.data.YearProduced,
      PreliminaryOrdered: this.props.data.YearPreliminaryOrdered,
      YearCapacityComments: this.props.data.YearCapacityComments,
    }
  }

  initialize() {
    let totalList = [];
    if(this.props.data){
      // Add row for each month
      totalList = this.props.data.Months;

      // Add row for year total (Month 13)
      const totalExists = totalList.some(item => item.Month === 13);
      if (!totalExists) {
        const yearEntry = this.createYearEntry();
        totalList.push(yearEntry);
      }
    }
    this.state.totals = totalList;
  }

  formatCommentsTooltip = (comments, title) => {
    if (!comments || comments.length === 0) {
      return '';
    }
    
    let html = '';
    if (title) {
      html += `<div style="font-weight: bold; margin-bottom: 5px;">${title}</div>`;
    }
    html += '<table class="commentTable">';
    html += '<thead><tr><th>Created</th><th>Created by</th><th>Customer</th><th>Comment</th></tr></thead>';
    html += '<tbody>';
    comments.forEach(c => {
      const ccr = c.CustomerShortName?.CLIENT_CODE_REGIONAL ?? '';
      const shortName = c.CustomerShortName?.SHORT_NAME ?? '';
      const customerText = ccr || shortName ? `${ccr} - ${shortName}` : '';
      html += `<tr><td>${c.Created || ''}</td><td>${c.CreatedBy || ''}</td><td>${customerText}</td><td>${c.Comment || ''}</td></tr>`;
    });
    html += '</tbody></table>';
    
    return html;
  };

  createColumn(header, accessor, maxWidth, isBold = false, showCapacityComments = false) {
    return {
      Header: header,
      accessor: accessor,
      maxWidth: maxWidth,
      headerStyle: { 
        whiteSpace: 'pre-line',
        ...(isBold && { fontWeight: 'bold'})
      },
      Cell: row => {
        let capacityComments = [];
        let commentTitle = '';
        
        if (showCapacityComments) {
          // For Total row (Month 13), use YearCapacityComments
          if (row.original.Month === 13) {
            capacityComments = row.original.YearCapacityComments || [];
            commentTitle = 'Year Capacity Comments:';
          } 
          // For month rows, use CapacityComments from the month
          else if (row.original.CapacityComments) {
            capacityComments = row.original.CapacityComments;
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            commentTitle = `${monthNames[row.original.Month - 1]} Capacity Comments:`;
          }
        }
        
        const hasComments = capacityComments.length > 0;
        const commentHtml = hasComments ? this.formatCommentsTooltip(capacityComments, commentTitle) : '';
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span className={`${accessor} flexSpan`} style={isBold ? { fontWeight: 'bold' } : {}}>
              {row.value?.toLocaleString('sv', { useGrouping: true }) || '0'}
            </span>
            {hasComments && (
              <FontAwesome 
                name="comments" 
                data-for="globalTip" 
                data-tip={commentHtml}
                style={{ color: '#337ab7', cursor: 'pointer' }}
              />
            )}
          </div>
        );
      }
    }
  }

  createCommonColumns() {
    const columns = [];

    if(hasGrant(Grants.TYPES.Admin_ViewCapacityAllocation)){
      columns.push(this.createColumn("Budget\ncapacity", "Capacity", 80, false, true));
      columns.push(this.createColumn("Actual\ncapacity", "ActualCapacity", 60));
      columns.push(this.createColumn("Budget allocated\nvolume", "AllocationBudget", 110));
    }
    
    columns.push(this.createColumn("Allocated\nvolume", "Allocation", 100));

    // "1": internal user
    if(this.props.userType === "1"){
      columns.push(this.createColumn("Actual available\nvolume", "DifferenceCapacityAllocated", 100));
    }

    columns.push(this.createColumn("Ordered\nvolume", "Ordered", 60));
    columns.push(this.createColumn("Preliminary/On-hold\nvolume", "PreliminaryOrdered", 120));

    if(hasGrant(Grants.TYPES.Admin_ViewCapacityAllocation)){
      columns.push(this.createColumn("Produced\nvolume", "Produced", 80));
    }
    
    columns.push(this.createColumn("Diff\n(allocated - ordered)", "DifferenceAllocatedOrdered", 120, true));

    return columns;
  }

  getMonthColumns() {
    const monthColumns = [
      {
        Header: "Month",
        accessor: "Month",
        maxWidth: 50,
        Cell: row => {
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Total'];
          const monthName = monthNames[row.value - 1];
          return (
            <div>
              <span className="Month flexSpan">
                {monthName || ''}
              </span>
            </div>
          );
        },
      },
      {
        expander: true,
        width: 35,
      }
    ];

    return monthColumns.concat(this.createCommonColumns());
  }
  
  getWeekColumns() {
    const weekColumns = [
        this.createColumn("Week", "Week", 75),
    ];

    return weekColumns.concat(this.createCommonColumns());
  }

  getBody(data){
    if (this.state.isLoading)
      {
        return (
            <LoadingSpinner isLoading={true} />
        );
      }
    else 
    {
    const infoTotals = `
        <div>
            <strong>Month View:</strong><br/>
            • Expand each month (▶) to see weekly breakdown<br/>
            • Month rows show aggregated values across all allocated volumes<br/><br/>
            
            <strong>Data Columns:</strong><br/>
            <div style="padding-left: 20px;">
                • <strong>Budget capacity:</strong> Budget machine capacity (operation)<br/>
                • <strong>Actual capacity:</strong> Actual machine capacity from paperline (operation)<br/>
                • <strong>Budget allocated volume:</strong> Budget allocated volume (sales)<br/>
                • <strong>Allocated volume:</strong> Current allocated volume (sales)<br/>
                • <strong>Actual available volume:</strong> Remaining capacity (Actual capacity - allocated volume)<br/>
                • <strong>Ordered volume:</strong> Order volume<br/>
                • <strong>Preliminary/On-hold volume:</strong> Preliminary order volume<br/>
                • <strong>Produced volume:</strong> Actual production output<br/>
                • <strong>Diff (allocated - ordered):</strong> Difference between allocated volume and order volume<br/>
            </div>
        </div>`;
      return(
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <legend className="allocationLegend" style={{ display: 'inline-block', width: 'auto', paddingBottom: 0 }}><Convert.TextTo value_metric={"Totals (t)"} value_imperial={"Totals (tn)"}/></legend>
            { // "1": internal user
             this.props.userType === "1" && 
              <FontAwesome 
                  name="info-circle" 
                  data-for="globalTip" 
                  data-tip={infoTotals}
                  style={{ cursor: 'pointer', fontSize: '16px' }}
              />
            }
          </div>
          <Dialog ref={(el) => { this.dialog = el }} />      
          <ReactTooltip id="globalTip" html place="bottom" effect="solid"/>
            <NPReactTable
            getTdProps={(state, rowInfo, column, instance) => {
              return {
                style: {
                  overflow: 'visible'                
                }
              }
            }}
            className="margin-top-5 np-react-table allocationTable"
            headerZIndex={1}
            data={data}
            ref={(r) => { this.reactTable = r }}
            showPagination={false}
            showPageSizeOptions={false}
            hasHeaderGroups={false}
            sortable={false}
            minRows={3} 
            columns={this.getMonthColumns()}
            defaultPageSize={10000}
            SubComponent={row => {
              const weekData = row.original.Weeks;
              return (
                <div style={{ padding: "10px" }}>
                  <NPReactTable
                    data={weekData}
                    columns={this.getWeekColumns()}
                    showPagination={false}
                    defaultPageSize={10000}
                    minRows={1}
                    className="np-react-table weekAllocationTable"
                    hasHeaderGroups={false}
                  />
                </div>
              );
            }} 
            onResizedChange={resized => { this.setState({ resized }) } }
            onPageChange={
              page => {
                this.setState({ page});
              }
            }
            onPageSizeChange={(pageSize, page) => {
                this.setState({ page, pageSize});
              }
            }
          />
        </div>
      )
    }
  }

  render()
  {
      const data = this.state.totals ? this.state.totals : [];
      return ( 
          <div>  
              {this.getBody(data)}
          </div>
      );
  }
}

export default AllocationWeekTotals;
